/**
 * 密码验证函数
 * @param {string} password 密码字符串
 * @returns {Object} 返回验证结果对象 {valid: boolean, message: string, strength: string}
 */
export function validatePassword(password) {
  // 1. 检查密码长度至少8位
  if (password.length < 8) {
    return {
      valid: false,
      message: '密码长度至少8位',
      strength: 'weak'
    }
  }

  // 2. 检查是否包含大小写字母和数字
  const hasUpperCase = /[A-Z]/.test(password)
  const hasLowerCase = /[a-z]/.test(password)
  const hasNumber = /[0-9]/.test(password)

  if (!hasUpperCase || !hasLowerCase || !hasNumber) {
    return {
      valid: false,
      message: '密码必须包含大小写字母和数字',
      strength: 'weak'
    }
  }

  // 3. 检查是否包含连续字符（如12345678, abcdefgh等）
  // 检查数字连续
  const numberSequences = ['0123456789', '1234567890', '2345678901', '3456789012', '4567890123',
    '5678901234', '6789012345', '7890123456', '8901234567', '9012345678']
  for (const seq of numberSequences) {
    if (seq.includes(password) || password.includes(seq.substring(0, 8))) {
      return {
        valid: false,
        message: '密码不能包含连续数字',
        strength: 'weak'
      }
    }
  }

  // 检查字母连续（小写）
  const alphabet = 'abcdefghijklmnopqrstuvwxyz'
  for (let i = 0; i <= alphabet.length - 8; i++) {
    const seq = alphabet.substring(i, i + 8)
    if (password.toLowerCase().includes(seq)) {
      return {
        valid: false,
        message: '密码不能包含连续字母',
        strength: 'weak'
      }
    }
  }

  // 检查字母连续（大写）
  const alphabetUpper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  for (let i = 0; i <= alphabetUpper.length - 8; i++) {
    const seq = alphabetUpper.substring(i, i + 8)
    if (password.includes(seq)) {
      return {
        valid: false,
        message: '密码不能包含连续字母',
        strength: 'weak'
      }
    }
  }

  // 4. 检查重复字符（如aaaaaaaa, 11111111等）
  const repeatPattern = /^(.)\1{7,}$/
  if (repeatPattern.test(password)) {
    return {
      valid: false,
      message: '密码不能使用重复字符',
      strength: 'weak'
    }
  }

  // 5. 检查常见弱密码
  const weakPasswords = [
    '12345678', '87654321', 'password', 'qwertyui', 'admin123',
    'abcdefgh', 'hijklmno', 'pqrstuvw', 'xyzabcde', '00000000',
    '11111111', '22222222', '33333333', '44444444', '55555555',
    '66666666', '77777777', '88888888', '99999999', 'aaaaaaaa',
    'bbbbbbbb', 'cccccccc', 'dddddddd', 'eeeeeeee', 'ffffffff',
    'gggggggg', 'hhhhhhhh', 'iiiiiiii', 'jjjjjjjj', 'kkkkkkkk',
    'llllllll', 'mmmmmmmm', 'nnnnnnnn', 'oooooooo', 'pppppppp',
    'qqqqqqqq', 'rrrrrrrr', 'ssssssss', 'tttttttt', 'uuuuuuuu',
    'vvvvvvvv', 'wwwwwwww', 'xxxxxx', 'yyyyyyyy', 'zzzzzzzz'
  ]

  if (weakPasswords.includes(password.toLowerCase())) {
    return {
      valid: false,
      message: '密码过于简单，请使用更复杂的密码',
      strength: 'weak'
    }
  }

  // 计算密码强度
  let strength = 'medium' // 默认中等强度

  // 评估密码强度
  const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
  const length = password.length

  let score = 0
  // 长度评分
  if (length >= 8) score += 1
  if (length >= 12) score += 1
  if (length >= 16) score += 1

  // 字符类型评分
  if (hasUpperCase && hasLowerCase) score += 1
  if (hasNumber) score += 1
  if (hasSpecialChar) score += 1

  // 确定强度等级
  if (score >= 5) {
    strength = 'strong'
  } else if (score >= 3) {
    strength = 'medium'
  } else {
    strength = 'weak'
  }

  return {
    valid: true,
    message: '密码符合要求',
    strength: strength
  }
}


/**
 * 密码生成器
 * @param passwordLength
 * @param characterTypes
 * @returns {string}
 */
export function generatePassword(passwordLength = 8, characterTypes = ['uppercase', 'lowercase', 'numbers', 'symbols']) {
  // 配置
  // const passwordLength = 8
  // const characterTypes = ['uppercase', 'lowercase', 'numbers', 'symbols']
  // 字符集
  const characterSets = {
    uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
    lowercase: 'abcdefghijklmnopqrstuvwxyz',
    numbers: '01234567890123456789',
    symbols: '!@#$%^&*()_+-=[]{}|;:,.<>?'
  }
  let chars = ''
  characterTypes.forEach(type => {
    chars += characterSets[type]
  })

  let password = ''
  for (let i = 0; i < passwordLength; i++) {
    const randomIndex = Math.floor(Math.random() * chars.length)
    password += chars[randomIndex]
  }
  return password
}

// 复制到剪贴板
export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text)
  } catch (err) {
    // 备用方案
    const textArea = document.createElement('textarea')
    textArea.value = text
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
  }
}
